import { ContentType, Placement } from '@ori/analytics-data';
import type { CopyLinkPanelProps, SharedContent, SharingPanelProps, VendorType } from '@ori/sharing-panel';
import { share, SharingCapability } from '@ori/sharing-panel';
import { useCallback, useState } from 'react';

import { type ShareButtonProps } from '../components/ShareButton';
import type { SharingWrapperProps } from '../components/SharingWrapper/types';
import { logDebug, mainLogger } from '../logger';
import { getSharedContent, showErrorNotification, translationKeys, triggerShareItemClickEvent } from '../utils';
import { useTranslations } from './useTranslations';

export const useBasketSharing = ({ isSharingButtonActive }: SharingWrapperProps) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCopyLinkOpen, setIsCopyLinkOpen] = useState(false);
  const [sharedContent, setSharedContent] = useState<Partial<SharedContent>>({});
  const [link, setLink] = useState('');
  const isPopupOpen = isPanelOpen || isCopyLinkOpen;

  const { formatMessage } = useTranslations();

  const onShareButtonClick = useCallback(async () => {
    logDebug('onShareButtonClick()');
    try {
      const data = await getSharedContent();
      setSharedContent(data);
      setIsPanelOpen(true);
    } catch (error: unknown) {
      showErrorNotification(translationKeys.genericError);
      mainLogger.error(error as Error, { action: 'onShareButtonClick' });
    }
  }, []);

  const onClose = useCallback(() => {
    logDebug('Closing sharing wrapper ...');
    setIsPanelOpen(false);
    setIsCopyLinkOpen(false);
  }, []);

  const onError = useCallback(() => {
    onClose();
    showErrorNotification(translationKeys.genericError);
  }, [onClose]);

  const onCopyLink = useCallback((vendor: VendorType, finalSharedLink: string) => {
    logDebug('onCopyLink()', { finalSharedLink, vendor });
    setLink(finalSharedLink);
    setIsCopyLinkOpen(true);
  }, []);

  const onShareIconClick = useCallback(
    async (vendor: VendorType) => {
      logDebug('onShareIconClick handler called.');
      // close sharing popup
      onClose();
      // always monitor user intentions even when the checks later will fail
      triggerShareItemClickEvent({
        contentType: ContentType.ProductList,
        method: vendor,
        placement: Placement.Minibag,
      });
      // start the sharing process
      void share({ onCopyLink, onError, sharedContent, vendor });
    },
    [onClose, onCopyLink, onError, sharedContent],
  );

  const shareButtonProps: ShareButtonProps = {
    isSharingButtonActive,
    onClick: onShareButtonClick,
  };

  const { ShareUrlAndText } = SharingCapability;
  const panelProps: SharingPanelProps = {
    // sharing capabilities filter so far hard-coded here
    capabilities: [ShareUrlAndText],
    captionText: formatMessage('shareWithBrandPartner'),
    isCopyLinkIconDisplayed: true,
    onError,
    onShareIconClick,
  };

  const copyLinkPopupProps = { header: formatMessage('copyLink') } as const;
  const sharePopupProps = { size: 'small' } as const;
  const popupProps = {
    disableEnforceFocus: true,
    disableTransition: true,
    onClose,
    open: isPopupOpen,
    ownerState: { isCopyLinkOpen },
    ...(isCopyLinkOpen ? copyLinkPopupProps : sharePopupProps),
  } as const;

  const copyLinkProps: CopyLinkPanelProps = {
    buttonText: formatMessage('copy'),
    copiedText: formatMessage('linkCopied'),
    link,
  };

  return {
    copyLinkProps,
    isCopyLinkOpen,
    isPanelOpen,
    isPopupOpen,
    panelProps,
    popupProps,
    shareButtonProps,
  };
};
